'use client';

import * as React from 'react';
import { ChangeEvent,  ForwardedRef, forwardRef, useCallback, useState } from 'react';
import NextImage, { StaticImageData } from 'next/image';

import { UpdateAvatarIcon, UserDefaultIcon } from 'components/icons/v2';
import { UserNode } from 'generated/types';
import { getInitials } from 'utils/string';

type AvatarUserNode = Pick<UserNode, 'firstName' | 'lastName'>

interface IMemberAvatar extends React.InputHTMLAttributes<HTMLInputElement> {
  user?: AvatarUserNode | null;
  avatar?: string | StaticImageData | null;
  alt?: string;
  width?: number;
  height?: number;
  containerClassname?: string;
  wrapperClassnames?: string;
  defaultIconWidth?: number;
  defaultIconHeight?: number;
  isEditable?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const Avatar= forwardRef((props: IMemberAvatar, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    alt,
    avatar,
    height,
    user,
    width,
    containerClassname = '',
    wrapperClassnames = '',
    defaultIconWidth = 28,
    defaultIconHeight = 28,
    isEditable = false,
    onChange,
    value,
    ...rest
  } = props;

  const [isError, setError] = useState<boolean>(false);

  const handleError = useCallback(() => {
    setError(prevState => !prevState);
  },[]);

  const Component = () => (
    <>
      {!!avatar && !isError ? (
        <div
          className={`rounded-full overflow-hidden ${containerClassname}`}
          style={{
            height: `${height}px`,
            width: `${width}px`
          }}
        >
          <NextImage
            src={avatar}
            className={'h-full object-cover'}
            alt={alt || `User Image - ${user?.firstName ? user?.firstName : ''}`}
            width={width}
            height={height}
            onError={handleError}
          />
        </div>
      ) : user?.firstName || user?.lastName ? (
        <div
          className={`rounded-full overflow-hidden border bg-gray-100 border-[#00000014] flex justify-center items-center ${containerClassname}`}
          style={{
            height: `${height}px`,
            width: `${width}px`,
            fontSize: `${(width ?? 24) / 2}px`
          }}
        >
          {getInitials(`${user.firstName} ${user.lastName}`)}
        </div>
      ) : (
        <div
          className={`rounded-full overflow-hidden flex justify-center items-center text-gray-500 bg-gray-100 border border-[#00000014] ${containerClassname}`}
          style={{
            height: `${height}px`,
            width: `${width}px`
          }}
        >
          <UserDefaultIcon height={defaultIconHeight} width={defaultIconWidth} />
        </div>
      )}
    </>
  );

  return (
    <div className={`relative group flex max-w-max max-h-max ${wrapperClassnames}`}>
      <Component />
      {isEditable && (
        <label
          className="absolute top-0 left-0 w-full h-full hidden group-hover:flex items-center justify-center cursor-pointer"
          htmlFor="file-input"
        >
          <div className="p-[10px] bg-black/50 backdrop-blur-[6px] rounded-full text-white">
            <UpdateAvatarIcon />
            <input type="file" className="hidden" ref={ref} id="file-input" {...rest} onChange={onChange} />
          </div>
        </label>
      )}
    </div>
  );
});

Avatar.displayName = 'Avatar';
